import * as THREE from "three";
import { CELL, LAYER_H } from "./loot";

/**
 * The brig, built procedurally around the roped-off cargo grid: hull, bow,
 * quarterdeck, mast, rails — and the grid itself as pickable tiles. World
 * frame: deck surface at y=0, grid centered on the origin, x across the beam
 * (cols), z along the ship toward the bow (rows).
 */

export interface ShipRig {
  group: THREE.Group;
  /** Pickable deck tiles; userData = { cell: { x, y } }. */
  tiles: THREE.Mesh[];
  /** Per-cell highlight planes, indexed [y][x]. */
  glow: THREE.Mesh[][];
  /** The rigging-line frame that marks the height limit. */
  limit: THREE.Group;
}

export const cellToWorld = (cols: number, rows: number, x: number, y: number): [number, number] =>
  [(x - cols / 2 + 0.5) * CELL, (y - rows / 2 + 0.5) * CELL];

export const worldToCell = (cols: number, rows: number, wx: number, wz: number): [number, number] =>
  [Math.floor(wx / CELL + cols / 2), Math.floor(wz / CELL + rows / 2)];

const wood = (color: number, rough = 0.85) =>
  new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.04 });

function plankTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#7d5a34";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 8; i++) {
    const y = i * 32;
    ctx.fillStyle = i % 2 ? "#755331" : "#835e38";
    ctx.fillRect(0, y, 256, 30);
    ctx.fillStyle = "rgba(38,24,12,.85)";
    ctx.fillRect(0, y + 30, 256, 2);
    ctx.strokeStyle = "rgba(46,30,15,.35)";
    for (let g = 0; g < 5; g++) {
      ctx.beginPath();
      const gy = y + 4 + Math.random() * 24;
      ctx.moveTo(0, gy);
      for (let x = 0; x <= 256; x += 32) ctx.lineTo(x, gy + (Math.random() - 0.5) * 3);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(38,24,12,.6)";
    for (let j = 0; j < 2; j++) ctx.fillRect(Math.floor(Math.random() * 250), y, 2, 30);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

let planksTex: THREE.Texture | null = null;

const HULL = 0x3a2417;
const HULL_TRIM = 0x59371e;
const RAIL = 0x4a2f1b;
const ROPE = 0xc9a061;

export function buildShip(cols: number, rows: number, maxH: number): ShipRig {
  const group = new THREE.Group();
  const W = cols * CELL, L = rows * CELL;
  const deckW = W + 1.8;                  // deck extends past the grid
  const sternLen = 2.6, bowLen = 3.2;
  const deckL = L + sternLen + bowLen - 1.2;
  const deckZ = (bowLen - sternLen) / 2 - 0.6; // deck center, grid stays at origin
  const shadowsOK = true;

  planksTex ??= plankTexture();
  const deckMat = new THREE.MeshStandardMaterial({ map: planksTex, roughness: 0.9, metalness: 0.02 });

  /* ---- deck slab ---- */
  const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, 0.34, deckL), deckMat);
  deck.position.set(0, -0.17, deckZ);
  deck.receiveShadow = shadowsOK;
  group.add(deck);

  /* ---- hull: a rounded cross-section (beam × draught) extruded along the
     keel and pinched toward the bow, so it reads as a wooden boat hull rather
     than a stray shape under the deck. ---- */
  const beam = deckW / 2;
  const draught = 1.55;
  const section = new THREE.Shape();
  section.moveTo(-beam, 0);                                   // port sheer (deck level)
  section.lineTo(beam, 0);                                    // starboard sheer
  section.lineTo(beam, -draught * 0.4);
  section.quadraticCurveTo(beam, -draught, 0, -draught);      // starboard bilge → keel
  section.quadraticCurveTo(-beam, -draught, -beam, -draught * 0.4); // keel → port bilge
  section.closePath();
  const hullLen = deckL + 0.6;
  const hullGeo = new THREE.ExtrudeGeometry(section, { depth: hullLen, bevelEnabled: false, curveSegments: 14 });
  hullGeo.translate(0, 0, -hullLen / 2);                      // centre on the keel
  // taper the beam toward the bow (+z): full stern, pinched prow.
  const pos = hullGeo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getZ(i) + hullLen / 2) / hullLen;          // 0 stern → 1 bow
    const pinch = 1 - Math.pow(Math.max(0, t - 0.45) / 0.55, 1.4) * 0.62;
    pos.setX(i, pos.getX(i) * pinch);
  }
  hullGeo.computeVertexNormals();
  const hull = new THREE.Mesh(hullGeo, wood(HULL));
  hull.position.set(0, -0.34, deckZ);                         // top of hull meets the deck underside
  hull.castShadow = shadowsOK;
  hull.receiveShadow = shadowsOK;
  group.add(hull);

  /* ---- bow: triangular prism + bowsprit ---- */
  const bowShape = new THREE.Shape();
  bowShape.moveTo(-deckW / 2, 0);
  bowShape.lineTo(deckW / 2, 0);
  bowShape.lineTo(0, 2.6);
  bowShape.closePath();
  const bowGeo = new THREE.ExtrudeGeometry(bowShape, { depth: 0.34, bevelEnabled: false });
  const bow = new THREE.Mesh(bowGeo, wood(HULL));
  bow.rotation.x = Math.PI / 2;
  bow.position.set(0, 0.0, deckZ + deckL / 2);
  bow.castShadow = shadowsOK;
  group.add(bow);
  const sprit = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 3.4, 8), wood(RAIL, 0.7));
  sprit.rotation.x = -Math.PI / 2 + 0.38;
  sprit.position.set(0, 0.6, deckZ + deckL / 2 + 2.6);
  group.add(sprit);

  /* ---- quarterdeck (stern), wheel, lantern ---- */
  const qd = new THREE.Mesh(new THREE.BoxGeometry(deckW, 0.9, 1.9), wood(HULL_TRIM, 0.8));
  qd.position.set(0, 0.45, deckZ - deckL / 2 + 0.95);
  qd.castShadow = shadowsOK;
  qd.receiveShadow = shadowsOK;
  group.add(qd);
  const wheelPost = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.7, 8), wood(RAIL));
  wheelPost.position.set(0, 1.2, qd.position.z + 0.4);
  group.add(wheelPost);
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.05, 8, 14), wood(0x6b4526, 0.6));
  wheel.position.set(0, 1.55, qd.position.z + 0.42);
  group.add(wheel);
  for (let i = 0; i < 4; i++) {
    const spoke = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 1.06, 6), wood(0x6b4526, 0.6));
    spoke.position.copy(wheel.position);
    spoke.rotation.z = (i / 4) * Math.PI;
    group.add(spoke);
  }
  const lanternPost = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 1.1, 6), wood(RAIL));
  lanternPost.position.set(deckW / 2 - 0.25, 1.4, qd.position.z - 0.6);
  group.add(lanternPost);
  const lantern = new THREE.Mesh(
    new THREE.SphereGeometry(0.16, 10, 8),
    new THREE.MeshStandardMaterial({ color: 0xffc66b, emissive: 0xff9d3a, emissiveIntensity: 1.6, roughness: 0.4 }));
  lantern.position.set(deckW / 2 - 0.25, 1.95, qd.position.z - 0.6);
  group.add(lantern);

  /* ---- mast (behind the grid) with furled sail + stays ---- */
  const mastZ = deckZ - deckL / 2 + 2.2;
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.17, 7.4, 10), wood(RAIL, 0.75));
  mast.position.set(0, 3.7, mastZ);
  mast.castShadow = shadowsOK;
  group.add(mast);
  const yard = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, deckW + 1.6, 8), wood(RAIL, 0.75));
  yard.rotation.z = Math.PI / 2;
  yard.position.set(0, 5.6, mastZ);
  group.add(yard);
  const sail = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, deckW + 1.1, 10), wood(0xd9c9a8, 0.95));
  sail.rotation.z = Math.PI / 2;
  sail.position.set(0, 5.38, mastZ);
  group.add(sail);
  const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.55), new THREE.MeshStandardMaterial({
    color: 0x14100c, roughness: 0.9, side: THREE.DoubleSide }));
  flag.position.set(0.45, 7.2, mastZ);
  group.add(flag);
  const stayMat = new THREE.LineBasicMaterial({ color: ROPE, transparent: true, opacity: 0.55 });
  const stays = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 7.3, mastZ), new THREE.Vector3(0, 1.2, deckZ + deckL / 2 + 2.4),
    new THREE.Vector3(0, 7.3, mastZ), new THREE.Vector3(0, 1.1, deckZ - deckL / 2 + 0.2),
  ]);
  group.add(new THREE.LineSegments(stays, stayMat));

  /* ---- bulwarks (side + stern rails) ---- */
  const railH = 0.52;
  for (const sx of [-1, 1]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(0.14, railH, deckL - 0.4), wood(RAIL, 0.8));
    side.position.set(sx * (deckW / 2 - 0.07), railH / 2, deckZ);
    side.castShadow = shadowsOK;
    group.add(side);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.07, deckL - 0.3), wood(HULL_TRIM, 0.7));
    cap.position.set(sx * (deckW / 2 - 0.07), railH + 0.035, deckZ);
    group.add(cap);
  }
  const sternRail = new THREE.Mesh(new THREE.BoxGeometry(deckW, railH + 0.3, 0.14), wood(RAIL, 0.8));
  sternRail.position.set(0, (railH + 0.3) / 2 + 0.9, deckZ - deckL / 2 + 0.07);
  group.add(sternRail);

  /* ---- deck props ---- */
  const coil = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.1, 8, 16), wood(0xb08c54, 0.95));
  coil.rotation.x = -Math.PI / 2;
  coil.position.set(-deckW / 2 + 0.55, 0.1, deckZ + deckL / 2 - 1.0);
  group.add(coil);
  const bucket = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.12, 0.26, 10), wood(0x7a5a36, 0.9));
  bucket.position.set(deckW / 2 - 0.5, 0.13, deckZ + deckL / 2 - 1.4);
  group.add(bucket);

  /* ---- the roped-off cargo grid ---- */
  const tiles: THREE.Mesh[] = [];
  const glow: THREE.Mesh[][] = [];
  const tileGeo = new THREE.PlaneGeometry(CELL * 0.98, CELL * 0.98);
  for (let y = 0; y < rows; y++) {
    glow.push([]);
    for (let x = 0; x < cols; x++) {
      const [wx, wz] = cellToWorld(cols, rows, x, y);
      const checker = (x + y) % 2 === 0;
      const tile = new THREE.Mesh(tileGeo, new THREE.MeshStandardMaterial({
        color: checker ? 0xf3e2b8 : 0xd9c193,
        transparent: true,
        opacity: checker ? 0.38 : 0.22,
        roughness: 0.9,
        depthWrite: false,
      }));
      tile.rotation.x = -Math.PI / 2;
      tile.position.set(wx, 0.012, wz);
      tile.userData.cell = { x, y };
      tile.receiveShadow = shadowsOK;
      group.add(tile);
      tiles.push(tile);

      const hl = new THREE.Mesh(tileGeo, new THREE.MeshBasicMaterial({
        color: 0xffd97a, transparent: true, opacity: 0, depthWrite: false,
      }));
      hl.rotation.x = -Math.PI / 2;
      hl.position.set(wx, 0.02, wz);
      hl.raycast = () => undefined; // visual only
      group.add(hl);
      glow[y].push(hl);
    }
  }
  // rope border around the grid
  const ropeMat = new THREE.MeshStandardMaterial({ color: ROPE, roughness: 0.9 });
  const mkRope = (len: number, alongX: boolean, px: number, pz: number) => {
    const r = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, len, 6), ropeMat);
    r.rotation.z = alongX ? Math.PI / 2 : 0;
    if (!alongX) r.rotation.x = Math.PI / 2;
    r.position.set(px, 0.045, pz);
    return r;
  };
  group.add(mkRope(W + 0.1, true, 0, -L / 2));
  group.add(mkRope(W + 0.1, true, 0, L / 2));
  group.add(mkRope(L + 0.1, false, -W / 2, 0));
  group.add(mkRope(L + 0.1, false, W / 2, 0));

  /* ---- the height limit: corner posts + rigging line ---- */
  const limit = new THREE.Group();
  const limH = maxH * LAYER_H;
  const postMat = wood(RAIL, 0.8);
  for (const [cx, cz] of [[-W / 2, -L / 2], [W / 2, -L / 2], [-W / 2, L / 2], [W / 2, L / 2]] as const) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, limH + 0.12, 8), postMat);
    post.position.set(cx, (limH + 0.12) / 2, cz);
    limit.add(post);
  }
  const limMat = new THREE.LineBasicMaterial({ color: ROPE, transparent: true, opacity: 0.5 });
  const limGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-W / 2, limH, -L / 2), new THREE.Vector3(W / 2, limH, -L / 2),
    new THREE.Vector3(W / 2, limH, -L / 2), new THREE.Vector3(W / 2, limH, L / 2),
    new THREE.Vector3(W / 2, limH, L / 2), new THREE.Vector3(-W / 2, limH, L / 2),
    new THREE.Vector3(-W / 2, limH, L / 2), new THREE.Vector3(-W / 2, limH, -L / 2),
  ]);
  limit.add(new THREE.LineSegments(limGeo, limMat));
  limit.userData.lineMat = limMat;
  group.add(limit);

  return { group, tiles, glow, limit };
}
